/**
 * Provider Service
 * 
 * Centralized service for provider-related functionality.
 * Provides reusable functions for finding providers and managing provider-related actions.
 */

import { AppStateType } from '../state/AppState';
import { getFindProviderUrl } from '../utils/websiteUtils';

/**
 * Generate find-a-provider URL with current state parameters
 * This centralizes the URL construction logic that was scattered across screens
 * 
 * @param state - Current application state
 * @returns Complete URL for find-a-provider page
 */
export function generateFindProviderUrl(state: AppStateType): string {
  return getFindProviderUrl(state.stateString);
}

/**
 * Opens the Mindful Therapy Group "Find a Provider" page in current tab
 * with the user's state information pre-populated.
 * 
 * @param state - Current application state (or partial state with stateString)
 */
export function openFindProviderPage(state: AppStateType): void {
  const url = generateFindProviderUrl(state);
  window.location.href = url;
}

/**
 * Opens the Mindful Therapy Group "Find a Provider" page in current tab
 * with just a state string (for backward compatibility).
 * 
 * @param stateString - The user's state string (e.g., "Washington", "California")
 */
export const findAnotherProvider = (stateString?: string | null): void => {
  const mockState = { stateString: stateString || null } as any;
  openFindProviderPage(mockState);
};

/**
 * Creates a handler function for the "Find Another Provider" button
 * that can be used in React components.
 * 
 * @param stateString - The user's state string from the app state
 * @returns A function that opens the provider search page
 */
export const createFindAnotherProviderHandler = (stateString?: string | null) => {
  return () => findAnotherProvider(stateString);
};
